import {
  createRouter, createRoute, createRootRoute,
  RouterProvider, Navigate,
} from '@tanstack/react-router'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import ErrorBoundary from './components/error/ErrorBoundary'
import AppShell from './components/layout/AppShell'
import Login from './components/auth/Login'
import MonitoringPage from './modules/monitoring/MonitoringPage'
import ContractsPage from './modules/contracts/ContractsPage'
import ProcessPage from './modules/process/ProcessPage'
import PlanningPage from './modules/planning/PlanningPage'
import TimePage from './modules/time/TimePage'
import IntegrationsPage from './modules/settings/IntegrationsPage'
import FortnoxPage from './modules/settings/FortnoxPage'
import HaileyPage from './modules/settings/HaileyPage'
import AzureDevOpsPage from './modules/settings/AzureDevOpsPage'
import TimeTrackerPage from './modules/settings/TimeTrackerPage'
import CalendarificPage from './modules/settings/CalendarificPage'
import AzureCostIntegrationPage from './modules/settings/AzureCostIntegrationPage'
import ClementineIntegrationPage from './modules/settings/ClementineIntegrationPage'
import FinanceSettingsPage from './modules/settings/FinanceSettingsPage'
import PublicHolidaysPage from './modules/settings/PublicHolidaysPage'
import RevenueAccountMatrixPage from './modules/settings/RevenueAccountMatrixPage'
import AuditPage from './modules/settings/AuditPage'
import ProjectsPage from './modules/settings/ProjectsPage'
import ProjectsBulkPage from './modules/settings/ProjectsBulkPage'
import ProjectOverviewPage from './modules/projects/ProjectOverviewPage'
import ModulesPage from './modules/settings/ModulesPage'
import CustomersPage from './modules/settings/CustomersPage'
import InvoicingPage from './modules/invoicing/InvoicingPage'
import InvoiceComposerPage from './modules/invoicing/InvoiceComposerPage'
import SalesPage from './modules/sales/SalesPage'
import AzureCostPage from './modules/cost/AzureCostPage'
import ClementinePage from './modules/clementine/ClementinePage'
import LeadsPage from './modules/leads/LeadsPage'
import CustomersModule from './modules/customers/CustomersModule'
import CustomerOverviewPage from './modules/customers/CustomerOverviewPage'
import PeoplePage from './modules/people/PeoplePage'
import DevOpsModule from './modules/devops/DevOpsModule'
import SubscriptionsPage from './modules/subscriptions/SubscriptionsPage'
import SubscriptionSettingsPage from './modules/subscriptions/SubscriptionSettingsPage'
import SubscriptionReconciliationPage from './modules/subscriptions/SubscriptionReconciliationPage'
import InvoicePreviewPage from './modules/subscriptions/InvoicePreviewPage'
import SubscriptionReportsPage from './modules/subscriptions/SubscriptionReportsPage'

// ─── Root layout ─────────────────────────────────────────────────────────────

function RootLayout() {
  const { accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()

  if (inProgress === 'startup' || inProgress === 'handleRedirect') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    )
  }

  if (!isAuthenticated) return <Login />

  const user = accounts[0]
  return <AppShell user={user} />
}

// ─── Routes ──────────────────────────────────────────────────────────────────

const rootRoute = createRootRoute({ component: RootLayout })

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => (
    <div>
      <h1 className="text-2xl font-semibold text-foreground mb-2">Dashboard</h1>
      <p className="text-muted-foreground">Welcome to Numberskills Forge.</p>
    </div>
  ),
})

const monitoringRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/monitoring',
  component: MonitoringPage,
})

const contractsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contracts',
  component: ContractsPage,
})

const processRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/process',
  component: ProcessPage,
})

const planningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning',
  component: PlanningPage,
})

const timeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/time',
  component: TimePage,
})

// /my-time was a separate page in WI-11822; folded into /time as the default
// "My time" tab in WI-11829. Keep the route so old bookmarks redirect.
const myTimeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-time',
  component: () => <Navigate to="/time" />,
})

const integrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations',
  component: IntegrationsPage,
})

const fortnoxIntegrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations/fortnox',
  component: FortnoxPage,
})

const haileyIntegrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations/hailey',
  component: HaileyPage,
})

const azureDevOpsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations/azure-devops',
  component: AzureDevOpsPage,
})

const timeTrackerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations/timetracker',
  component: TimeTrackerPage,
})

const calendarificRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations/calendarific',
  component: CalendarificPage,
})

const azureCostIntegrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations/azure-cost',
  component: AzureCostIntegrationPage,
})

const clementineIntegrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations/clementine',
  component: ClementineIntegrationPage,
})

const financeSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/finance',
  component: FinanceSettingsPage,
})

const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects',
  component: ProjectsPage,
})

const projectsLegacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/projects',
  component: () => <Navigate to="/projects" />,
})

const projectsManageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/manage',
  component: ProjectsBulkPage,
})

const projectOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$id',
  component: () => {
    const { id } = projectOverviewRoute.useParams()
    return <ProjectOverviewPage projectId={Number(id)} />
  },
})

const modulesSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/modules',
  component: ModulesPage,
})

const customersSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/customers',
  component: CustomersPage,
})

const publicHolidaysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/public-holidays',
  component: PublicHolidaysPage,
})

const revenueAccountMatrixRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/revenue-account-matrix',
  component: RevenueAccountMatrixPage,
})

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/audit',
  component: AuditPage,
})

const invoicingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoicing',
  component: InvoicingPage,
})

const invoiceComposerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoicing/composer',
  component: InvoiceComposerPage,
})

const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers',
  component: CustomersModule,
})

const customerOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/$id',
  component: () => {
    const { id } = customerOverviewRoute.useParams()
    return <CustomerOverviewPage customerId={Number(id)} />
  },
})

const peopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people',
  component: PeoplePage,
})

const salesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sales',
  component: SalesPage,
})

const costRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cost',
  component: AzureCostPage,
})

const clementineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clementine',
  component: ClementinePage,
})

const leadsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leads',
  component: LeadsPage,
})

const devopsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/devops',
  component: DevOpsModule,
})

const subscriptionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/subscriptions',
  component: SubscriptionsPage,
})

const subscriptionReconciliationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/subscriptions/reconciliation',
  component: SubscriptionReconciliationPage,
})

const subscriptionSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/billing',
  component: SubscriptionSettingsPage,
})

const productInvoicePreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/subscriptions/invoice-preview',
  component: InvoicePreviewPage,
})

const subscriptionReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/subscriptions/reports',
  component: SubscriptionReportsPage,
})

const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: () => <Navigate to="/" />,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  monitoringRoute,
  contractsRoute,
  processRoute,
  planningRoute,
  timeRoute,
  myTimeRoute,
  invoicingRoute,
  invoiceComposerRoute,
  salesRoute,
  costRoute,
  customersRoute,
  customerOverviewRoute,
  peopleRoute,
  integrationsRoute,
  fortnoxIntegrationRoute,
  haileyIntegrationRoute,
  azureDevOpsRoute,
  timeTrackerRoute,
  calendarificRoute,
  azureCostIntegrationRoute,
  clementineIntegrationRoute,
  financeSettingsRoute,
  projectsRoute,
  projectsLegacyRoute,
  projectsManageRoute,
  projectOverviewRoute,
  modulesSettingsRoute,
  customersSettingsRoute,
  publicHolidaysRoute,
  revenueAccountMatrixRoute,
  auditRoute,
  clementineRoute,
  leadsRoute,
  devopsRoute,
  // /subscriptions/reconciliation must come before /subscriptions so the
  // catch-all in TanStack's path matching doesn't swallow it.
  subscriptionReconciliationRoute,
  productInvoicePreviewRoute,
  subscriptionReportsRoute,
  subscriptionsRoute,
  subscriptionSettingsRoute,
  catchAllRoute,
])

export const router = createRouter({ routeTree })

export default function App() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  )
}
